// ============================================================================
// Market P&L — can this market carry a seat? (board item #1027)
//
// Built to the approved demo, section for section:
// tcs-app-demo-market-pl-seat-check-2026-11-02.html. Ben, 2 Nov 2026, option A:
// internal labor = seat standards × a per-person market allocation kept in the
// app; revenue and sub cost live from the finance book; payroll is not read.
//
// Who sees it: is_market_pl_viewer() — finance capability, executives (role or
// employee type Exec), market leads. It is the Dashboard's own "Market P&L" tab
// (Ben 2026-11-03), shown only when that is true; RLS on the two tables is the hard boundary. Who edits: the finance
// capability only (the database refuses anyone else; the screen shows values).
// ============================================================================

import { Fragment, useMemo, useState, type KeyboardEvent, type ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';
import { WidgetCard, WidgetSkeleton } from '../../widget-card';
import { Button, MarketStripe } from '../../../../shared/components';
import { useMarkets } from '../../../../shared/markets';
import { fmtDate, localToday } from '../../../../shared/format';
import { WriteDidNotLandError } from '../../../../shared/save-path';
import {
  STANDARDS,
  SEAT_LABEL,
  computeMarket,
  homeMarkets,
  hiringLines,
  isEditedFromStandard,
  lineBase,
  lineCost,
  overAllocated,
  pct,
  sameAs,
  seatCost,
  seatScenario,
  tone,
  usd,
  verdictText,
  type Assumptions,
  type LineRole,
  type MarketResult,
  type SeatKey,
  type Settings,
  type Tone,
} from './market-pl-math';
import {
  addAllocationLine,
  saveAssumptions,
  updateAllocationLine,
  useMarketPlData,
  usePeopleOptions,
  type LineRow,
} from './use-market-pl';

interface Props {
  canEdit: boolean;
  myEid: string | null;
}

const TONE_PILL: Record<Tone, string> = {
  g: 'bg-st-ok-bg text-st-ok-fg',
  a: 'bg-st-warn-bg text-st-warn-fg',
  r: 'bg-st-danger-bg text-st-danger-fg',
};
const TONE_DOT: Record<Tone, string> = {
  g: 'bg-st-ok-solid',
  a: 'bg-st-warn-solid',
  r: 'bg-st-danger-solid',
};
const TONE_BOX: Record<Tone, string> = {
  g: 'border-st-ok-border bg-st-ok-bg',
  a: 'border-st-warn-border bg-st-warn-bg',
  r: 'border-st-danger-border bg-st-danger-bg',
};

const SEATS: SeatKey[] = ['NM', 'AM', 'BDD'];

function describe(e: unknown): string {
  if (e instanceof WriteDidNotLandError) return e.described.message;
  return e instanceof Error ? e.message : 'Something went wrong.';
}

export function MarketPlWidget({ canEdit, myEid }: Props) {
  const { data, isLoading, error } = useMarketPlData(true);
  const [seat, setSeat] = useState<SeatKey>('NM');
  const [hours, setHours] = useState(30);
  const [burdenOn, setBurdenOn] = useState(false);
  const [signedOn, setSignedOn] = useState(false);
  // Ben 2026-11-03, option A: on by default — a new seat replaces the borrowed share.
  const [replaceBorrowed, setReplaceBorrowed] = useState(true);
  const [open, setOpen] = useState<string | null>(null);
  const homeOf = useMemo(() => (data ? homeMarkets(data.markets) : new Map<string, string>()), [data]);

  const s: Settings | null = data
    ? { a: data.assumptions, seat, hours, burdenOn, signedOn, replaceBorrowed, homeOf }
    : null;
  const results = useMemo(
    () => (data && s ? data.markets.map((m) => computeMarket(m, s)) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, seat, hours, burdenOn, signedOn, replaceBorrowed],
  );

  return (
    <WidgetCard
      title="Market P&L — can this market carry a seat?"
      subtitle="Pre-SG&A contribution by market, and what happens to it if you add one Night Manager, Account Manager or BDD."
    >
      {error && <p className="text-xs text-st-danger-fg">Couldn't load: {error}</p>}
      {!error && isLoading && !data && <WidgetSkeleton lines={8} />}
      {!error && data && s && (
        <div className="space-y-4">
          <DataNote updatedByName={data.updatedByName} updatedAt={data.updatedAt} />
          <SeatPicker
            s={s}
            setSeat={setSeat}
            setHours={setHours}
            setBurdenOn={setBurdenOn}
            signedOn={signedOn}
            setSignedOn={setSignedOn}
            setReplaceBorrowed={setReplaceBorrowed}
          />
          <Kpis results={results} s={s} />
          <MarketTable results={results} s={s} open={open} setOpen={setOpen} />
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <AssumptionsPanel a={data.assumptions} canEdit={canEdit} myEid={myEid} />
            <AllocationPanel lines={data.lines} s={s} canEdit={canEdit} myEid={myEid} />
          </div>
        </div>
      )}
    </WidgetCard>
  );
}

// ── 7. Data note ────────────────────────────────────────────────────────────
function DataNote({ updatedByName, updatedAt }: { updatedByName: string | null; updatedAt: string | null }) {
  return (
    <p className="rounded-md border border-tcs-border bg-tcs-bg-muted px-3 py-2 text-xs text-tcs-text-secondary">
      Revenue and sub cost are live from the finance book (annualized run-rate). Internal labor comes from the
      allocation below. Standards last edited
      {updatedByName ? ` by ${updatedByName}` : ''}
      {updatedAt ? ` on ${fmtDate(updatedAt)}` : ''}.
    </p>
  );
}

// ── 1. Seat picker ──────────────────────────────────────────────────────────
function SeatPicker({
  s,
  setSeat,
  setHours,
  setBurdenOn,
  signedOn,
  setSignedOn,
  setReplaceBorrowed,
}: {
  s: Settings;
  setSeat: (k: SeatKey) => void;
  setHours: (h: number) => void;
  setBurdenOn: (b: boolean) => void;
  signedOn: boolean;
  setSignedOn: (b: boolean) => void;
  setReplaceBorrowed: (b: boolean) => void;
}) {
  return (
    <section
      aria-label="Seat to test"
      className="flex flex-wrap items-center gap-4 rounded-lg border border-tcs-border bg-tcs-raised p-3"
    >
      <span className="text-sm font-semibold text-tcs-text">Add one…</span>
      <div role="tablist" aria-label="Seat" className="inline-flex gap-0.5 rounded-lg bg-tcs-bg-muted p-0.5">
        {SEATS.map((k) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={s.seat === k}
            onClick={() => setSeat(k)}
            className={`min-h-[38px] rounded-md px-3 py-1.5 text-left text-sm ${
              s.seat === k
                ? 'bg-tcs-raised font-semibold text-tcs-text shadow-sm'
                : 'text-tcs-text-secondary hover:text-tcs-text'
            }`}
          >
            {SEAT_LABEL[k]}
            <span className="block text-[11px] tabular-nums text-tcs-text-muted">
              {usd(seatCost(k, s))}
              {k === 'NM' && s.hours !== s.a.nm_full_hours ? ` at ${s.hours} h` : ''}
            </span>
          </button>
        ))}
      </div>
      {s.seat === 'NM' && (
        <label className="flex items-center gap-2 text-xs text-tcs-text-secondary">
          NM hours / week
          <input
            type="range"
            min={4}
            max={40}
            step={1}
            value={s.hours}
            onChange={(e) => setHours(Number(e.target.value))}
            className="w-36 accent-tcs-accent"
            aria-valuetext={`${s.hours} hours`}
          />
          <output className="min-w-[11ch] tabular-nums text-tcs-text">
            {s.hours} h → {usd(seatCost('NM', s))}
          </output>
        </label>
      )}
      <label className="flex cursor-pointer items-center gap-2 rounded-md border border-tcs-border bg-tcs-bg-muted px-2.5 py-1.5 text-xs text-tcs-text">
        <input type="checkbox" checked={s.burdenOn} onChange={(e) => setBurdenOn(e.target.checked)} />
        Include W2 benefits &amp; taxes
        <span className="tabular-nums text-tcs-text-muted">+{s.a.w2_burden_pct}% on base</span>
      </label>
      <label className="flex cursor-pointer items-center gap-2 rounded-md border border-tcs-border bg-tcs-bg-muted px-2.5 py-1.5 text-xs text-tcs-text">
        <input type="checkbox" checked={signedOn} onChange={(e) => setSignedOn(e.target.checked)} />
        Signed, not started
      </label>
      <label
        className="flex cursor-pointer items-center gap-2 rounded-md border border-tcs-border bg-tcs-bg-muted px-2.5 py-1.5 text-xs text-tcs-text"
        title="A new seat takes over the share this market borrows from another market's person; that share goes back to their home market."
      >
        <input type="checkbox" checked={!!s.replaceBorrowed} onChange={(e) => setReplaceBorrowed(e.target.checked)} />
        Replace borrowed coverage
      </label>
      <div className="flex flex-wrap gap-3 text-[11px] text-tcs-text-secondary xl:ml-auto">
        <span><i className={`mr-1 inline-block h-2 w-2 rounded-full ${TONE_DOT.g}`} />Carries it today</span>
        <span><i className={`mr-1 inline-block h-2 w-2 rounded-full ${TONE_DOT.a}`} />Short under $5k/mo</span>
        <span><i className={`mr-1 inline-block h-2 w-2 rounded-full ${TONE_DOT.r}`} />Short $5k+/mo or already negative</span>
        <span>Tags = Bryan's revenue lines · verdict = does the book pay for it</span>
      </div>
    </section>
  );
}

// ── 2. KPI strip ────────────────────────────────────────────────────────────
function Kpis({ results, s }: { results: MarketResult[]; s: Settings }) {
  const tot = results.reduce(
    (t, r) => ({ rev: t.rev + r.rev, gp: t.gp + r.gp, c: t.c + r.contrib }),
    { rev: 0, gp: 0, c: 0 },
  );
  const active = results.reduce((t, r) => t + r.input.sitesActive, 0);
  const signed = results.reduce((t, r) => t + r.input.sitesSigned, 0);
  const uncosted = results.reduce((t, r) => t + r.input.sitesSignedUncosted, 0);
  const carry = results.filter((r) => seatScenario(r, s.seat, s).after >= 0);
  const neg = results.filter((r) => r.contrib < 0);
  const tile = (l: string, v: string, sub: string) => (
    <div className="rounded-lg border border-tcs-border bg-tcs-raised p-3">
      <div className="text-xs text-tcs-text-muted">{l}</div>
      <div className="text-lg font-semibold tabular-nums text-tcs-text">{v}</div>
      <div className="text-xs text-tcs-text-secondary">{sub}</div>
    </div>
  );
  return (
    <section className="grid grid-cols-2 gap-3 xl:grid-cols-4">
      {tile(
        'Pro forma revenue',
        usd(tot.rev),
        `${active} active${s.signedOn ? ` + ${signed} signed` : ''} sites${
          s.signedOn && uncosted ? ` · ${uncosted} signed left out (no rate or sub cost yet)` : ''
        }`,
      )}
      {tile(
        `Pre-SG&A contribution${s.burdenOn ? ' (with W2 burden)' : ''}`,
        usd(tot.c),
        `${pct(tot.rev ? tot.c / tot.rev : 0)} margin · gross ${pct(tot.rev ? tot.gp / tot.rev : 0)}`,
      )}
      {tile(
        `Can carry a ${SEAT_LABEL[s.seat]} today`,
        `${carry.length} of ${results.length}`,
        carry.map((r) => r.input.marketName.replace(/ Metro$/, '')).join(', ') || 'None',
      )}
      {tile(
        "Already carrying a seat they don't pay for",
        String(neg.length),
        neg.map((r) => r.input.marketName.replace(/ Metro$/, '')).join(', ') || 'None',
      )}
    </section>
  );
}

// ── 3. Market table + 4. row detail ─────────────────────────────────────────
function Pill({ t, children }: { t: Tone; children: ReactNode }) {
  return (
    <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold ${TONE_PILL[t]}`}>
      {children}
    </span>
  );
}

function MarketTable({
  results,
  s,
  open,
  setOpen,
}: {
  results: MarketResult[];
  s: Settings;
  open: string | null;
  setOpen: (k: string | null) => void;
}) {
  const max = Math.max(1, ...results.map((r) => Math.abs(r.contrib)));
  const tot = results.reduce(
    (t, r) => ({ rev: t.rev + r.rev, gp: t.gp + r.gp, int: t.int + r.internal, c: t.c + r.contrib, sa: t.sa + r.input.sitesActive, ss: t.ss + r.input.sitesSigned }),
    { rev: 0, gp: 0, int: 0, c: 0, sa: 0, ss: 0 },
  );
  const toggle = (k: string) => setOpen(open === k ? null : k);
  const openResult = results.find((r) => r.input.marketName === open) ?? null;
  const onKey = (e: KeyboardEvent, k: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggle(k);
    }
  };
  const th = 'whitespace-nowrap px-3 py-2 text-right text-xs font-medium text-tcs-text-muted';
  const td = 'whitespace-nowrap px-3 py-2.5 text-right tabular-nums';
  return (
    <section className="overflow-hidden rounded-lg border border-tcs-border">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-tcs-border px-3 py-2">
        <h3 className="text-sm font-semibold text-tcs-text">{results.length} markets</h3>
        <span className="text-xs text-tcs-text-muted">Tap a market for its P&amp;L, all three seat scenarios and the break-even.</span>
      </div>
      <div className="overflow-x-auto [contain:inline-size]">
        <table className="w-full border-collapse text-sm text-tcs-text">
          <thead>
            <tr className="border-b border-tcs-border">
              <th className={`${th} text-left`}>Market</th>
              <th className={th}>Sites</th>
              <th className={th}>Revenue</th>
              <th className={th}>Gross margin</th>
              <th className={th}>Internal labor</th>
              <th className={th}>Contribution today</th>
              <th className={th}>After {SEAT_LABEL[s.seat]}</th>
              <th className={th}>Verdict</th>
              <th className={`${th} text-left`}>Bryan's lines</th>
            </tr>
          </thead>
          <tbody>
            {results.map((r) => {
              const k = r.input.marketName;
              const sc = seatScenario(r, s.seat, s);
              const t = tone(r, sc);
              const w = Math.max(2, (Math.abs(r.contrib) / max) * 100);
              const isOpen = open === k;
              return (
                <Fragment key={k}>
                  <tr
                    tabIndex={0}
                    aria-expanded={isOpen}
                    onClick={() => toggle(k)}
                    onKeyDown={(e) => onKey(e, k)}
                    className={`cursor-pointer border-b border-tcs-border focus-visible:outline focus-visible:outline-2 focus-visible:outline-tcs-accent ${
                      isOpen ? 'bg-tcs-accent-bg' : 'hover:bg-tcs-surface-hover'
                    }`}
                  >
                    <td className="px-3 py-2.5 text-left">
                      <span className="flex items-stretch gap-2">
                        <MarketStripe region={k} />
                        <span className="font-semibold">{k}</span>
                      </span>
                    </td>
                    <td className={td}>{r.input.sitesActive}{s.signedOn ? ` + ${r.input.sitesSigned}` : ''}</td>
                    <td className={td}>{usd(r.rev)}</td>
                    <td className={td}>{pct(r.gm)}</td>
                    <td className={td}>{usd(-r.internal)}</td>
                    <td className={td}>
                      <span className="inline-grid min-w-[170px] grid-cols-[76px_1fr] items-center gap-2">
                        <span className="text-right">{usd(r.contrib)}</span>
                        <span className="relative h-2 overflow-hidden rounded bg-tcs-bg-muted">
                          <i
                            className={`absolute inset-y-0 left-0 rounded ${r.contrib < 0 ? 'bg-st-danger-solid' : 'bg-st-ok-solid'}`}
                            style={{ width: `${w}%` }}
                          />
                        </span>
                      </span>
                    </td>
                    <td className={`${td} font-semibold`}>{usd(sc.after)}</td>
                    <td className={td}>
                      {sc.after >= 0 ? (
                        <Pill t="g"><i className={`h-2 w-2 rounded-full ${TONE_DOT.g}`} />Carries it</Pill>
                      ) : (
                        <Pill t={t}><i className={`h-2 w-2 rounded-full ${TONE_DOT[t]}`} />Short <span className="font-medium tabular-nums">{usd(sc.gapMo)}/mo</span></Pill>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-left">
                      {hiringLines(r.rev, s.a).map((tag) => (
                        <span
                          key={tag.label}
                          className={`mr-1 inline-block rounded-full px-1.5 py-0.5 text-[11px] font-semibold ${TONE_PILL[tag.reached ? 'g' : 'r']}`}
                        >
                          {tag.label}{tag.reached ? ' ✓' : ''}
                        </span>
                      ))}
                      <ChevronRight
                        aria-hidden
                        className={`inline h-4 w-4 text-tcs-text-muted transition-transform motion-reduce:transition-none ${isOpen ? 'rotate-90' : ''}`}
                      />
                    </td>
                  </tr>
                  {isOpen && (
                    // On a phone the detail stacks below the scrolling table
                    // (rendered after it); from lg up it opens under its row.
                    <tr className="hidden border-b border-tcs-border lg:table-row">
                      <td colSpan={9} className="p-0">
                        <RowDetail r={r} s={s} results={results} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            <tr className="bg-tcs-bg-muted font-semibold">
              <td className="px-3 py-2.5 text-left">Total</td>
              <td className={td}>{tot.sa}{s.signedOn ? ` + ${tot.ss}` : ''}</td>
              <td className={td}>{usd(tot.rev)}</td>
              <td className={td}>{pct(tot.rev ? tot.gp / tot.rev : 0)}</td>
              <td className={td}>{usd(-tot.int)}</td>
              <td className={td}>
                {usd(tot.c)} <span className="font-normal text-tcs-text-muted">{pct(tot.rev ? tot.c / tot.rev : 0)}</span>
              </td>
              <td className={td} />
              <td className={td} />
              <td className={td} />
            </tr>
          </tbody>
        </table>
      </div>
      {openResult && (
        <div className="border-t border-tcs-border lg:hidden">
          <RowDetail r={openResult} s={s} results={results} />
        </div>
      )}
    </section>
  );
}

function RowDetail({ r, s, results }: { r: MarketResult; s: Settings; results: MarketResult[] }) {
  const v = verdictText(r, s.seat, s);
  const plRow = 'border-b border-dashed border-tcs-border';
  const num = 'py-1.5 pl-4 text-right tabular-nums';
  const share = (x: number) => <td className={`${num} text-tcs-text-muted`}>{pct(r.rev ? x / r.rev : 0)}</td>;
  return (
    <div className="grid grid-cols-1 gap-5 bg-tcs-raised p-4 text-left lg:grid-cols-2">
      <div>
        <h4 className="mb-2 text-sm font-semibold">P&amp;L, annualized</h4>
        <table className="w-full text-sm">
          <tbody>
            <tr className={plRow}>
              <td className="py-1.5">
                Revenue ({r.input.sitesActive} active{s.signedOn && r.input.sitesSigned ? `, ${r.input.sitesSigned} signed` : ''})
                {s.signedOn && r.input.sitesSignedUncosted ? (
                  <span className="block text-xs text-tcs-text-muted">
                    {r.input.sitesSignedUncosted} signed building{r.input.sitesSignedUncosted === 1 ? '' : 's'} left out: no rate or sub cost recorded yet
                  </span>
                ) : null}
              </td>
              <td className={num}>{usd(r.rev)}</td>
              <td className={`${num} text-tcs-text-muted`}>100%</td>
            </tr>
            <tr className={plRow}><td className="py-1.5">Subcontractor labor</td><td className={num}>{usd(-r.sub)}</td>{share(-r.sub)}</tr>
            <tr className={plRow}><td className="py-1.5">Supplies ({s.a.supplies_pct}%)</td><td className={num}>{usd(-r.supplies)}</td><td className={`${num} text-tcs-text-muted`}>({s.a.supplies_pct}%)</td></tr>
            <tr className="border-b border-tcs-border-strong font-semibold"><td className="py-1.5">Gross profit</td><td className={num}>{usd(r.gp)}</td><td className={`${num} text-tcs-text-muted`}>{pct(r.gm)}</td></tr>
            {r.input.lines.length === 0 && (
              <tr className={plRow}><td className="py-1.5 pl-3 text-tcs-text-secondary">No internal labor charged to this market</td><td className={num}>$0</td><td /></tr>
            )}
            {r.input.lines.map((l) => {
              const c = lineCost(l, s);
              return (
                <tr key={l.allocationId} className={plRow}>
                  <td className="whitespace-normal py-1.5 pl-3 text-tcs-text-secondary">
                    {l.role === 'POOL' ? '' : `${l.role} — `}{l.displayName}
                    {l.override == null && l.share !== 1 ? `, ${(l.share * 100).toFixed(1)}%` : ''}
                    {l.override != null && l.role !== 'POOL' ? ' (actual)' : ''}
                    {s.burdenOn ? <span className="text-tcs-text-muted"> +{s.a.w2_burden_pct}%</span> : null}
                  </td>
                  <td className={num}>{usd(-c)}</td>
                  {share(-c)}
                </tr>
              );
            })}
            <tr className="font-semibold"><td className="py-1.5">Pre-SG&amp;A contribution</td><td className={num}>{usd(r.contrib)}</td><td className={`${num} text-tcs-text-muted`}>{pct(r.rev ? r.contrib / r.rev : 0)}</td></tr>
          </tbody>
        </table>
      </div>
      <div>
        <h4 className="mb-2 text-sm font-semibold">Add one seat{s.burdenOn ? ` (base + ${s.a.w2_burden_pct}% burden)` : ''}</h4>
        <div className="overflow-x-auto [contain:inline-size]">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-tcs-border text-xs text-tcs-text-muted">
                <th className="py-1.5 text-left font-medium">Seat</th>
                <th className="py-1.5 pl-3 text-right font-medium">Cost</th>
                {s.replaceBorrowed && <th className="py-1.5 pl-3 text-right font-medium">Replaces</th>}
                {s.replaceBorrowed && <th className="py-1.5 pl-3 text-right font-medium">Net cost</th>}
                <th className="py-1.5 pl-3 text-right font-medium">Contribution after</th>
                <th className="py-1.5 pl-3 text-right font-medium">Margin</th>
                <th className="py-1.5 pl-3 text-right font-medium">Break-even revenue</th>
                <th className="py-1.5 pl-3 text-right font-medium">Gap / mo</th>
                <th className="py-1.5 pl-3" />
              </tr>
            </thead>
            <tbody>
              {SEATS.map((k) => {
                const sc = seatScenario(r, k, s);
                const t = tone(r, sc);
                return (
                  <tr key={k} className={`border-b border-dashed border-tcs-border ${k === s.seat ? 'bg-tcs-accent-bg font-semibold' : ''}`}>
                    <td className="whitespace-nowrap py-1.5">
                      {SEAT_LABEL[k]}
                      {k === 'NM' && s.hours !== s.a.nm_full_hours ? <span className="font-normal text-tcs-text-muted"> ({s.hours} h)</span> : null}
                    </td>
                    <td className="py-1.5 pl-3 text-right tabular-nums">{usd(-sc.gross)}</td>
                    {s.replaceBorrowed && (
                      <td className="py-1.5 pl-3 text-right tabular-nums">
                        {sc.replaced.length === 0
                          ? '—'
                          : sc.replaced.map((x) => (
                              <span key={x.line.allocationId} className="block whitespace-nowrap">
                                {x.line.displayName.split(' ')[0]} {(x.line.share * 100).toFixed(0)}% +{usd(x.cost)}
                              </span>
                            ))}
                      </td>
                    )}
                    {s.replaceBorrowed && <td className="py-1.5 pl-3 text-right tabular-nums">{usd(-sc.cost)}</td>}
                    <td className="py-1.5 pl-3 text-right tabular-nums">{usd(sc.after)}</td>
                    <td className="py-1.5 pl-3 text-right tabular-nums">{pct(sc.margin)}</td>
                    <td className="py-1.5 pl-3 text-right tabular-nums">{usd(sc.be)}</td>
                    <td className="py-1.5 pl-3 text-right tabular-nums">{sc.gapMo > 0 ? usd(sc.gapMo) : '—'}</td>
                    <td className="py-1.5 pl-3 text-right"><Pill t={t}>{sc.after >= 0 ? 'Carries' : 'Short'}</Pill></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className={`mt-3 rounded-lg border p-3 text-sm text-tcs-text ${TONE_BOX[v.tone]}`}>
          <b className="block">{v.lead}</b>
          {v.body}
        </div>
        <TakeBack r={r} s={s} results={results} />
        {s.seat === 'NM' && (
          <table className="mt-3 w-full text-sm">
            <thead>
              <tr className="border-b border-tcs-border text-xs text-tcs-text-muted">
                <th className="py-1.5 text-left font-medium">NM hours / week</th>
                <th className="py-1.5 pl-3 text-right font-medium">Cost</th>
                <th className="py-1.5 pl-3 text-right font-medium">Contribution after</th>
              </tr>
            </thead>
            <tbody>
              {[4, 10, 20, 30].map((hh) => {
                const hs = seatScenario(r, 'NM', s, hh);
                return (
                  <tr key={hh} className="border-b border-dashed border-tcs-border">
                    <td className="py-1.5">{hh}</td>
                    <td className="py-1.5 pl-3 text-right tabular-nums">{usd(-hs.cost)}</td>
                    <td className="py-1.5 pl-3 text-right tabular-nums">{usd(hs.after)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <p className="mt-2 text-xs text-tcs-text-secondary">
          Same as: {sameAs(r.input, seatScenario(r, s.seat, s).gapMo)} (this market's average monthly account).
        </p>
      </div>
    </div>
  );
}

/** The other half of a hire: each replaced share goes back to its person's home market. */
function TakeBack({ r, s, results }: { r: MarketResult; s: Settings; results: MarketResult[] }) {
  const sc = seatScenario(r, s.seat, s);
  if (sc.replaced.length === 0) return null;
  const byHome = new Map<string, { names: string[]; cost: number }>();
  for (const x of sc.replaced) {
    const cur = byHome.get(x.home) ?? { names: [], cost: 0 };
    cur.names.push(x.line.displayName.split(' ')[0]);
    cur.cost += x.cost;
    byHome.set(x.home, cur);
  }
  return (
    <ul className="mt-2 space-y-1 text-xs text-tcs-text-secondary">
      {[...byHome].map(([home, v]) => {
        const h = results.find((x) => x.input.marketName === home);
        return (
          <li key={home}>
            {home.replace(/ Metro$/, '')} takes back {v.names.join(' and ')}&apos;s {usd(v.cost)}
            {h ? ` → ${home.replace(/ Metro$/, '')} contribution ${usd(h.contrib)} → ${usd(h.contrib - v.cost)}` : ''}.
          </li>
        );
      })}
    </ul>
  );
}

// ── 5. Assumptions panel ────────────────────────────────────────────────────
const FIELDS: { k: keyof Assumptions; label: string; unit: string; step: number; help: string }[] = [
  { k: 'nm_full_annual', label: 'Night Manager', unit: '/yr', step: 1000, help: 'Base cost at full-time hours. The slider scales it to planned hours. Named actual-cost lines stay fixed.' },
  { k: 'nm_full_hours', label: 'Night Manager full-time hours', unit: 'h/wk', step: 1, help: 'The hours the Night Manager cost above buys.' },
  { k: 'am_annual', label: 'Account Manager', unit: '/yr', step: 1000, help: 'Base. Also re-prices the AM lines charged by share.' },
  { k: 'bdd_annual', label: 'BDD', unit: '/yr', step: 1000, help: 'Base. Also re-prices the BDD lines charged by share.' },
  { k: 'sdr_annual', label: 'SDR', unit: '/yr', step: 1000, help: 'Base. Also re-prices the SDR lines charged by share.' },
  { k: 'supplies_pct', label: 'Supplies', unit: '% rev', step: 0.1, help: 'Applied to every market.' },
  { k: 'w2_burden_pct', label: 'W2 benefits & taxes', unit: '% base', step: 1, help: 'Employer taxes, insurance and benefits on top of base, applied to every labor line and the new seat when the box is ticked. A planning figure until Gusto’s loaded rate replaces it.' },
  { k: 'line_first_nm', label: "Bryan's line — 1st NM", unit: '/yr rev', step: 50000, help: 'Annual revenue a market reaches before it earns the seat.' },
  { k: 'line_first_am', label: "Bryan's line — 1st AM", unit: '/yr rev', step: 50000, help: '' },
  { k: 'line_second_nm', label: "Bryan's line — 2nd NM", unit: '/yr rev', step: 100000, help: '' },
  { k: 'line_second_am', label: "Bryan's line — 2nd AM", unit: '/yr rev', step: 100000, help: '' },
];

function AssumptionsPanel({ a, canEdit, myEid }: { a: Assumptions; canEdit: boolean; myEid: string | null }) {
  const [draft, setDraft] = useState<Partial<Record<keyof Assumptions, string>>>({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const edited = isEditedFromStandard(a);

  const commit = async (k: keyof Assumptions) => {
    const raw = draft[k];
    if (raw === undefined) return;
    const v = Number(raw);
    if (!Number.isFinite(v) || v < 0 || (k === 'nm_full_hours' && v === 0)) {
      setMsg('Enter a number of zero or more (hours above zero).');
      return;
    }
    if (v === a[k]) {
      setDraft((d) => ({ ...d, [k]: undefined }));
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      await saveAssumptions({ [k]: v }, myEid);
      setDraft((d) => ({ ...d, [k]: undefined }));
    } catch (e) {
      setMsg(describe(e));
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    setBusy(true);
    setMsg(null);
    try {
      await saveAssumptions({ ...STANDARDS }, myEid);
      setDraft({});
    } catch (e) {
      setMsg(describe(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-lg border border-tcs-border">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-tcs-border px-3 py-2">
        <h3 className="text-sm font-semibold text-tcs-text">
          Assumptions
          {edited && (
            <span className="ml-2 rounded-full bg-st-warn-bg px-2 py-0.5 text-[11px] font-medium text-st-warn-fg">edited from standard</span>
          )}
        </h3>
        {canEdit && (
          <Button variant="secondary" size="sm" onClick={reset} disabled={busy || !edited}>
            Reset to standards
          </Button>
        )}
      </div>
      <div className="p-3">
        <table className="w-full text-sm">
          <tbody>
            {FIELDS.map((f) => (
              <tr key={f.k} className="border-b border-dashed border-tcs-border align-top">
                <td className="py-2 pr-3 text-tcs-text">{f.label}</td>
                <td className="whitespace-nowrap py-2 text-right">
                  {canEdit ? (
                    <input
                      type="number"
                      inputMode="decimal"
                      step={f.step}
                      min={0}
                      aria-label={f.label}
                      disabled={busy}
                      value={draft[f.k] ?? String(a[f.k])}
                      onChange={(e) => setDraft((d) => ({ ...d, [f.k]: e.target.value }))}
                      onBlur={() => commit(f.k)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                      }}
                      className="w-[11ch] rounded-md border border-tcs-border-input bg-tcs-field px-2 py-1 text-right tabular-nums text-tcs-text"
                    />
                  ) : (
                    <span className="tabular-nums text-tcs-text">{a[f.k].toLocaleString('en-US')}</span>
                  )}
                  <span className="ml-1 text-xs text-tcs-text-muted">{f.unit}</span>
                </td>
                <td className="hidden py-2 pl-3 text-xs text-tcs-text-secondary sm:table-cell">{f.help}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="mt-3 rounded-md bg-tcs-bg-muted px-3 py-2 font-mono text-xs text-tcs-text">
          Break-even revenue = current revenue + max(0, seat cost − contribution) ÷ gross margin %
        </p>
        {!canEdit && <p className="mt-2 text-xs text-tcs-text-muted">Finance changes these; everyone else reads them.</p>}
        {msg && <p className="mt-2 text-xs text-st-danger-fg" role="alert">{msg}</p>}
      </div>
    </section>
  );
}

// ── 6. Allocation panel ─────────────────────────────────────────────────────
const ROLES: LineRole[] = ['NM', 'AM', 'BDD', 'SDR', 'POOL'];

function AllocationPanel({ lines, s, canEdit, myEid }: { lines: LineRow[]; s: Settings; canEdit: boolean; myEid: string | null }) {
  const [msg, setMsg] = useState<string | null>(null);
  const over = overAllocated(lines);
  const byMarket = useMemo(() => {
    const m = new Map<string, LineRow[]>();
    for (const l of lines) m.set(l.marketName, [...(m.get(l.marketName) ?? []), l]);
    return [...m.entries()].sort((x, y) => x[0].localeCompare(y[0]));
  }, [lines]);

  return (
    <section className="rounded-lg border border-tcs-border">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-tcs-border px-3 py-2">
        <h3 className="text-sm font-semibold text-tcs-text">Who is charged to each market</h3>
        <span className="text-xs text-tcs-text-muted">Cost = actual, else the standard × share. Before W2 burden.</span>
      </div>
      <div className="space-y-3 p-3">
        {over.length > 0 && (
          <p className="rounded-md border border-st-warn-border bg-st-warn-bg px-3 py-2 text-xs text-st-warn-fg" role="status">
            Charged more than 100% across markets: {over.join(', ')}.
          </p>
        )}
        {byMarket.map(([market, ls]) => (
          <div key={market}>
            <div className="mb-1 flex items-center gap-2 text-xs font-semibold text-tcs-text">
              <MarketStripe region={market} />
              {market}
              <span className="ml-auto tabular-nums font-normal text-tcs-text-muted">
                {usd(ls.reduce((t, l) => t + lineBase(l, s.a), 0))}
              </span>
            </div>
            <ul className="divide-y divide-tcs-border/60">
              {ls.map((l) => (
                <AllocationRow key={l.allocationId} l={l} s={s} canEdit={canEdit} myEid={myEid} onError={setMsg} />
              ))}
            </ul>
          </div>
        ))}
        {canEdit && <AddLineForm myEid={myEid} onError={setMsg} />}
        {!canEdit && <p className="text-xs text-tcs-text-muted">Finance adds, changes and ends lines; everyone else reads them.</p>}
        {msg && <p className="text-xs text-st-danger-fg" role="alert">{msg}</p>}
      </div>
    </section>
  );
}

function AllocationRow({
  l,
  s,
  canEdit,
  myEid,
  onError,
}: {
  l: LineRow;
  s: Settings;
  canEdit: boolean;
  myEid: string | null;
  onError: (m: string | null) => void;
}) {
  const [share, setShare] = useState<string | null>(null);
  const [override, setOverride] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [confirmEnd, setConfirmEnd] = useState(false);
  const [busy, setBusy] = useState(false);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    onError(null);
    try {
      await fn();
    } catch (e) {
      onError(describe(e));
    } finally {
      setBusy(false);
    }
  };

  const saveShare = () => {
    if (share === null) return;
    const v = Number(share);
    if (!(v > 0 && v <= 1)) {
      onError('A share is more than 0 and at most 1 (for example 0.5 for half).');
      return;
    }
    if (v === l.share) return setShare(null);
    run(async () => {
      await updateAllocationLine(l.allocationId, { share: v }, myEid);
      setShare(null);
    });
  };
  const saveOverride = () => {
    if (override === null) return;
    const v = override.trim() === '' ? null : Number(override);
    if (v !== null && !(Number.isFinite(v) && v >= 0)) {
      onError('An actual cost is a dollar amount, or blank to use the standard.');
      return;
    }
    if (v === l.override) return setOverride(null);
    run(async () => {
      await updateAllocationLine(l.allocationId, { annual_cost_override: v }, myEid);
      setOverride(null);
    });
  };
  const saveNote = () => {
    if (note === null) return;
    const v = note.trim() || null;
    if (v === l.note) return setNote(null);
    run(async () => {
      await updateAllocationLine(l.allocationId, { note: v }, myEid);
      setNote(null);
    });
  };

  const input = 'rounded-md border border-tcs-border-input bg-tcs-field px-1.5 py-0.5 text-right tabular-nums text-tcs-text';
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1 py-1.5 text-xs text-tcs-text">
      <span className="min-w-[10rem] flex-1">
        <span className="mr-1 rounded bg-tcs-bg-muted px-1 py-0.5 text-[10px] font-semibold text-tcs-text-secondary">{l.role}</span>
        {l.displayName}
        {!canEdit && l.note ? <span className="block text-tcs-text-muted">{l.note}</span> : null}
      </span>
      {canEdit ? (
        <>
          <label className="flex items-center gap-1 text-tcs-text-muted">
            share
            <input
              type="number" step={0.01} min={0} max={1} disabled={busy} aria-label={`Share for ${l.displayName}`}
              value={share ?? String(l.share)}
              onChange={(e) => setShare(e.target.value)} onBlur={saveShare}
              className={`${input} w-[10ch]`}
            />
          </label>
          <label className="flex items-center gap-1 text-tcs-text-muted">
            actual $
            <input
              type="number" step={1000} min={0} disabled={busy} aria-label={`Actual annual cost for ${l.displayName}`}
              placeholder="standard"
              value={override ?? (l.override == null ? '' : String(l.override))}
              onChange={(e) => setOverride(e.target.value)} onBlur={saveOverride}
              className={`${input} w-[10ch]`}
            />
          </label>
          <span className="w-[9ch] text-right tabular-nums">{usd(lineBase(l, s.a))}</span>
          <input
            type="text" disabled={busy} aria-label={`Note for ${l.displayName}`} placeholder="note"
            value={note ?? l.note ?? ''}
            onChange={(e) => setNote(e.target.value)} onBlur={saveNote}
            className="min-w-[10rem] flex-1 rounded-md border border-tcs-border-input bg-tcs-field px-1.5 py-0.5 text-tcs-text"
          />
          {confirmEnd ? (
            <span className="flex gap-1">
              <Button size="sm" variant="secondary" disabled={busy}
                onClick={() => run(() => updateAllocationLine(l.allocationId, { effective_end: localToday() }, myEid, 'end the labor line'))}>
                Confirm end
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setConfirmEnd(false)}>Keep</Button>
            </span>
          ) : (
            <Button size="sm" variant="secondary" disabled={busy} onClick={() => setConfirmEnd(true)}>End line</Button>
          )}
        </>
      ) : (
        <>
          <span className="tabular-nums text-tcs-text-muted">
            {l.override != null ? 'actual' : `${(l.share * 100).toFixed(1)}%`}
          </span>
          <span className="w-[9ch] text-right tabular-nums">{usd(lineBase(l, s.a))}</span>
        </>
      )}
    </li>
  );
}

function AddLineForm({ myEid, onError }: { myEid: string | null; onError: (m: string | null) => void }) {
  const people = usePeopleOptions(true);
  const { markets } = useMarkets();
  const [who, setWho] = useState('');
  const [label, setLabel] = useState('');
  const [role, setRole] = useState<LineRole>('AM');
  const [market, setMarket] = useState('');
  const [share, setShare] = useState('1');
  const [override, setOverride] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const sh = Number(share);
    const ov = override.trim() === '' ? null : Number(override);
    if (!who && !label.trim()) return onError('Pick a person, or name the line (for a pool).');
    if (!market) return onError('Pick the market the line is charged to.');
    if (!(sh > 0 && sh <= 1)) return onError('A share is more than 0 and at most 1.');
    if (ov !== null && !(Number.isFinite(ov) && ov >= 0)) return onError('An actual cost is a dollar amount.');
    if (role === 'POOL' && ov === null) return onError('A pool line needs its actual annual cost.');
    setBusy(true);
    onError(null);
    try {
      await addAllocationLine(
        { employeeId: who || null, label: who ? null : label.trim(), role, marketName: market, share: sh, override: ov, note: note.trim() || null },
        myEid,
      );
      setWho(''); setLabel(''); setShare('1'); setOverride(''); setNote('');
    } catch (e) {
      onError(describe(e));
    } finally {
      setBusy(false);
    }
  };

  const field = 'rounded-md border border-tcs-border-input bg-tcs-field px-2 py-1 text-xs text-tcs-text';
  return (
    <div className="rounded-md border border-dashed border-tcs-border p-2">
      <div className="mb-1 text-xs font-semibold text-tcs-text">Add a line</div>
      <div className="flex flex-wrap items-center gap-2">
        <select aria-label="Person" value={who} onChange={(e) => setWho(e.target.value)} className={field}>
          <option value="">Person… (or name a pool)</option>
          {people.map((p) => <option key={p.employeeId} value={p.employeeId}>{p.name}</option>)}
        </select>
        {!who && (
          <input aria-label="Line name" placeholder="name, for a pool" value={label} onChange={(e) => setLabel(e.target.value)} className={field} />
        )}
        <select aria-label="Role" value={role} onChange={(e) => setRole(e.target.value as LineRole)} className={field}>
          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <select aria-label="Market" value={market} onChange={(e) => setMarket(e.target.value)} className={field}>
          <option value="">Market…</option>
          {markets.filter((m) => m.is_active).map((m) => <option key={m.market_name} value={m.market_name}>{m.market_name}</option>)}
        </select>
        <input aria-label="Share" type="number" step={0.01} min={0} max={1} value={share} onChange={(e) => setShare(e.target.value)} className={`${field} w-[8ch] text-right`} />
        <input aria-label="Actual annual cost" type="number" step={1000} min={0} placeholder="actual $ (optional)" value={override} onChange={(e) => setOverride(e.target.value)} className={`${field} w-[16ch] text-right`} />
        <input aria-label="Note" placeholder="note" value={note} onChange={(e) => setNote(e.target.value)} className={`${field} min-w-[10rem] flex-1`} />
        <Button size="sm" onClick={submit} disabled={busy}>Add line</Button>
      </div>
    </div>
  );
}
